/**
 * Fichier : chat_widget/src/widget/hooks/useChatSender.ts
 *
 * Principe :
 * Ce hook isole la logique d'envoi REST vers l'API `/chat`.
 *
 * Il gère :
 * - l'envoi d'un message (texte) ou d'une action (ex: more_infos_garage, book_try_vehicle)
 * - l'optimistic UI côté client (ajout immédiat du message user si demandé)
 * - l'ajout du message assistant retourné par l'API (reply + meta)
 * - la gestion du contexte de formulaire (formActive / formCarId) et son reset quand `lead_done`
 * - les helpers UX : stopTyping() et scrollToBottom()
 *
 * Objectif :
 * Garder `useChatPanel` centré sur l'orchestration et éviter la duplication de fetch/parsing
 * dans plusieurs handlers.
 */
import { useCallback, useEffect, useRef } from "react";
import { API_CHAT_URL } from "../../config";
import type { Vehicle, VehicleLike } from "../types/VehicleTypes";
import type { Msg } from "../types/messagesTypes";
import type { Socket } from "socket.io-client";
import type { FormActiveType } from "../types/formTypes";

type ChatApiResponse = {
  reply?: string;
  highlights?: Array<{
    carId: string;
    title: string;
    reason?: string;
  }>;
  meta?: {
    results?: Vehicle[];
    step?: string;
    schedule_info?: object;
    carId?: string;
    intent?: string;
    vehicle?: Vehicle;
  };
};

type SendParams = {
  text?: string | null;
  action?: string;
  carId?: string;
  appendUserBubble?: boolean;
};

type UseChatSenderParams = {
  sessionId: string;
  userId: string;

  setMessages: React.Dispatch<React.SetStateAction<Msg[]>>;
  setLoading: (v: boolean) => void;

  formActive: FormActiveType | null;
  formCarId: string | null;
  selectedCar: VehicleLike | null;
  attachedCar: VehicleLike | null;
  setFormActive: (v: FormActiveType | null) => void;
  socketRef: React.RefObject<Socket | null>;
  roomIdRef: React.RefObject<string | null>;
  chatIdRef: React.RefObject<string | null>;
  isBusy: boolean;

  stopTyping?: () => void;
  scrollToBottom?: () => void;
  clearSelectedCar: () => void;
};

export function useChatSender({
  sessionId,
  userId,
  setMessages,
  setLoading,
  formActive,
  formCarId,
  selectedCar,
  setFormActive,
  isBusy,
  stopTyping,
  scrollToBottom,
  socketRef,
  chatIdRef,
  roomIdRef,
}: UseChatSenderParams) {
  const sendAbortRef = useRef<AbortController | null>(null);

  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);

  const armInactivityTimer = useCallback(() => {
    // Reset the 2 min timer on every sendMessage call
    clearInactivityTimer();

    inactivityTimeoutRef.current = setTimeout(
      () => {
        const socket = socketRef.current;
        const chatId = chatIdRef.current;
        const roomId = roomIdRef.current;

        // If no new sendMessage call happened for 2 minutes, notify via socket
        if (socket && chatId && roomId) {
          socket.emit("chat:inactif", { sessionId, roomId, chatId, userId });
        }
      },
      2 * 60 * 1000,
    );
  }, [
    chatIdRef,
    clearInactivityTimer,
    sessionId,
    socketRef,
    roomIdRef,
    userId,
  ]);

  useEffect(() => {
    return () => {
      clearInactivityTimer();
    };
  }, [clearInactivityTimer]);

  // If a form becomes active or a car gets selected, stop inactivity timer
  useEffect(() => {
    if (formActive || selectedCar?._id) {
      clearInactivityTimer();
    }
  }, [formActive, selectedCar?._id, clearInactivityTimer]);

  const cancelSendRequests = useCallback(() => {
    if (sendAbortRef.current) {
      sendAbortRef.current.abort();
      sendAbortRef.current = null;
    }
    clearInactivityTimer();
    // Ensure UI doesn't stay stuck
    setLoading(false);
  }, [clearInactivityTimer, setLoading]);

  const sendMessage = useCallback(
    async (params: SendParams) => {
      armInactivityTimer();
      const rawText = params.text ?? "";
      const text = typeof rawText === "string" ? rawText.trim() : "";
      stopTyping?.();
      if (isBusy) return null;

      if (text) {
        setMessages((m) => [...m, { role: "user", text } as unknown as Msg]);
        scrollToBottom?.();
      }

      setLoading(true);

      // Abort any previous in-flight send
      if (sendAbortRef.current) {
        sendAbortRef.current.abort();
      }
      const controller = new AbortController();
      sendAbortRef.current = controller;

      try {
        const payload = {
          sessionId,
          message: text || null,
          userId,
          ...(params.carId ? { carId: params.carId } : {}),
          ...(params.action ? { action: params.action } : {}),
          ...(formActive && formCarId
            ? { carId: formCarId, action: formActive }
            : formActive
              ? { action: formActive }
              : {}),
        };

        const res = await fetch(`${API_CHAT_URL}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const bodyText = await res.text().catch(() => "");
          throw new Error(`API error ${res.status} ${bodyText}`.trim());
        }

        const data = (await res.json()) as ChatApiResponse;

        const reply = data.reply || "";
        const intent = data.meta?.intent;

        // Keep your existing behavior: if garage form is requested
        if (intent === "lead_more_infos_garage") {
          setFormActive({
            name: "lead_more_infos_garage",
            label: "Un vendeur peut vous rappeler !",
          });
        }
        if (intent === "lead_more_infos_vehicle") {
          setFormActive({
            name: "lead_more_infos_vehicle",
            label: `En savoir plus`,
          });
        }
        if (intent === "lead_garage_trade_in") {
          setFormActive({
            name: "lead_garage_trade_in",
            label: "Estmation de votre véhicule",
          });
        }

        const step = data?.meta?.step ?? null;
        const scheduleInfo = data?.meta?.schedule_info || undefined;
        const results = Array.isArray(data?.meta?.results)
          ? data.meta!.results
          : undefined;
        const highlights = Array.isArray(data?.highlights)
          ? data!.highlights
          : undefined;

        if (reply?.length) {
          setMessages((m) => [
            ...m,
            {
              role: "assistant",
              text: reply,
              results,
              highlights,
              step,
              scheduleInfo,
              intent,
            } as unknown as Msg,
          ]);
          scrollToBottom?.();
        }

        return data;
      } catch (e: any) {
        if (e?.name === "AbortError") return null;

        console.error(e);
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            text: "Désolé, je n’arrive pas à joindre le service pour le moment.",
          } as unknown as Msg,
        ]);
        scrollToBottom?.();
        return null;
      } finally {
        // Clear controller only if it is still the active one
        if (sendAbortRef.current === controller) {
          sendAbortRef.current = null;
        }
        setLoading(false);
      }
    },
    [
      armInactivityTimer,
      formActive,
      formCarId,
      isBusy,
      scrollToBottom,
      sessionId,
      setFormActive,
      setLoading,
      setMessages,
      stopTyping,
      userId,
    ],
  );

  return { sendMessage, cancelSendRequests };
}
